import { Upload as S3Upload } from "@aws-sdk/lib-storage";
import { useCallback } from "react";

import { MULTIPART_UPLOAD_THRESHOLD, STORAGE_BUCKET } from "../constants";
import { getS3Client, refreshS3Client } from "../services/s3Client";
import supabase from "../services/supabaseClient";

export const useFileUpload = () => {
  /**
   * Uploads a file to storage
   * @param {Object} params - Upload parameters
   * @param {File} params.file - The file to upload
   * @param {string} params.filePath - The path where the file should be stored
   * @param {boolean} params.isUpdate - Whether this is an update operation
   * @returns {Promise<string>} The CloudFront URL of the uploaded file
   */
  const uploadFile = useCallback(async ({ file, filePath, isUpdate }) => {
    try {
      if (file.size >= MULTIPART_UPLOAD_THRESHOLD) {
        // Use multipart upload for large files
        const s3Client = await getS3Client();

        const upload = new S3Upload({
          client: s3Client,
          params: {
            Bucket: STORAGE_BUCKET,
            Key: filePath,
            Body: file,
          },
          queueSize: 4, // number of concurrent parts to upload
        });

        await upload.done();
      } else {
        // Use regular upload for smaller files
        if (isUpdate) {
          const { error } = await supabase.storage.from(STORAGE_BUCKET).update(filePath, file);
          if (error) throw error;
        } else {
          const { error } = await supabase.storage.from(STORAGE_BUCKET).upload(filePath, file);
          if (error) throw error;
        }
      }

      // Generate CloudFront URL
      const { data: signedData, error: signError } = await supabase.storage
        .from(STORAGE_BUCKET)
        .createSignedUrl(filePath, 3600);

      if (signError) throw signError;

      // Convert to CloudFront URL
      return signedData.signedUrl.replace(
        `${import.meta.env.VITE_SUPABASE_PROJECT_ID}.supabase.co/storage/v1/object/sign`,
        import.meta.env.VITE_CLOUDFRONT_DOMAIN
      );
    } catch (error) {
      if (error.message?.includes("expired")) {
        // Handle token expiration
        try {
          await refreshS3Client();
          // Retry the upload with new client
          // TODO: ... implement retry logic ...
        } catch (refreshError) {
          console.error("Failed to refresh S3 client:", refreshError);
          throw error;
        }
      }
      throw error;
    }
  }, []);

  return {
    uploadFile,
  };
};
